import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import mammoth from 'mammoth';
import {
  getQuestionsByCategory,
  createQuestion,
  createQuestionsBulk,
  updateQuestion,
  deleteQuestion
} from '../../services/api';

interface Question {
  _id: string;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
  createdAt: string;
  updatedAt: string;
}

interface Category {
  _id: string;
  name: string;
  description: string;
}

const QuestionsPage: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const category = location.state?.category as Category;
  
  const [questions, setQuestions] = useState<Question[]>([]);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [questionForm, setQuestionForm] = useState({ question: '', options: ['', '', '', ''], correctAnswer: 0, explanation: '' });
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Question | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedQuestions, setUploadedQuestions] = useState<any[]>([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);
  const deleteModalRef = useRef<HTMLDivElement>(null);
  const uploadModalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (categoryId) {
      fetchQuestions();
    }
  }, [categoryId]);

  // Handle escape key and click outside for modals
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showQuestionForm) {
          setShowQuestionForm(false);
        }
        if (showDeleteModal) {
          setShowDeleteModal(false);
        }
        if (showUploadModal) {
          setShowUploadModal(false);
          setUploadedQuestions([]);
          setUploadError('');
          setUploadedFile(null);
        }
        if (showSuccessModal) {
          setShowSuccessModal(false);
        }
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node) && showQuestionForm) {
        setShowQuestionForm(false);
      }
      if (deleteModalRef.current && !deleteModalRef.current.contains(e.target as Node) && showDeleteModal) {
        setShowDeleteModal(false);
      }
      if (uploadModalRef.current && !uploadModalRef.current.contains(e.target as Node) && showUploadModal) {
        setShowUploadModal(false);
        setUploadedQuestions([]);
        setUploadError('');
        setUploadedFile(null);
      }
    };

    if (showQuestionForm || showDeleteModal || showUploadModal || showSuccessModal) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [showQuestionForm, showDeleteModal, showUploadModal, showSuccessModal]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const res = await getQuestionsByCategory(categoryId!);
      setQuestions(Array.isArray(res) ? res : []);
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort questions
  const filteredQuestions = questions
    .filter(q =>
      q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.options.some(opt => opt.toLowerCase().includes(searchTerm.toLowerCase())) ||
      q.explanation.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

  const handleQuestionFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!categoryId) return;
    
    try {
      if (editingQuestion) {
        await updateQuestion(editingQuestion._id, { ...questionForm, category: categoryId });
      } else {
        await createQuestion({ ...questionForm, category: categoryId });
      }
      setShowQuestionForm(false);
      setEditingQuestion(null);
      setQuestionForm({ question: '', options: ['', ''], correctAnswer: 0, explanation: '' });
      fetchQuestions();
    } catch (error) {
      console.error('Error saving question:', error);
    }
  };

  const handleEditQuestion = (q: Question) => {
    setEditingQuestion(q);
    setQuestionForm({
      question: q.question,
      options: q.options,
      correctAnswer: q.correctAnswer,
      explanation: q.explanation
    });
    setShowQuestionForm(true);
  };

  const handleDeleteQuestion = (q: Question) => {
    setItemToDelete(q);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      await deleteQuestion(itemToDelete._id);
      fetchQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
    } finally {
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  // Parse Word document and extract questions
  const parseWordDocument = async (file: File) => {
    try {
      setUploadLoading(true);
      setUploadError('');

      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      const text = result.value;

      // Split text by question separators (--- or ###)
      const questionBlocks = text.split(/(?:---|\#\#\#)/).filter(block => block.trim());

      const parsedQuestions = questionBlocks.map((block, index) => {
        const lines = block.trim().split('\n').filter(line => line.trim());
        
        let question = '';
        let options: string[] = [];
        let correctAnswer = 0;
        let explanation = '';

        let currentSection = 'question';
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();
          
          if (line.match(/^[A-D]\)/)) {
            // This is an option
            currentSection = 'options';
            const optionText = line.substring(line.indexOf(')') + 1).trim();
            options.push(optionText);
          } else if (line.toLowerCase().includes('correct answer:') || line.toLowerCase().includes('answer:')) {
            // This is the correct answer
            currentSection = 'correctAnswer';
            const answerMatch = line.match(/[A-D]/i);
            if (answerMatch) {
              correctAnswer = answerMatch[0].toUpperCase().charCodeAt(0) - 65; // A=0, B=1, etc.
            }
          } else if (line.toLowerCase().includes('explanation:') || line.toLowerCase().includes('explain:')) {
            // This is the explanation
            currentSection = 'explanation';
            explanation = line.substring(line.indexOf(':') + 1).trim();
          } else if (currentSection === 'explanation') {
            // Continue adding to explanation
            explanation += ' ' + line;
          } else if (currentSection === 'question' && line) {
            // This is part of the question
            question += (question ? ' ' : '') + line;
          }
        }

        return {
          question: question.trim(),
          options: options.length === 4 ? options : ['', '', '', ''],
          correctAnswer: Math.max(0, Math.min(3, correctAnswer)), // Ensure it's 0-3
          explanation: explanation.trim(),
          isValid: question.trim() && options.length === 4 && options.every(opt => opt.trim())
        };
      }).filter(q => q.isValid);

      setUploadedQuestions(parsedQuestions);
      
      if (parsedQuestions.length === 0) {
        setUploadError('No valid questions found in the document. Please check the format.');
      }
    } catch (error) {
      console.error('Error parsing Word document:', error);
      setUploadError('Error parsing the Word document. Please make sure it\'s a valid .docx file.');
    } finally {
      setUploadLoading(false);
    }
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
          file.name.endsWith('.docx')) {
        setUploadedFile(file);
        parseWordDocument(file);
      } else {
        setUploadError('Please upload a valid .docx file.');
      }
    }
  };

  // Import questions from Word document
  const importQuestions = async () => {
    try {
      setUploadLoading(true);
      setUploadError('');

      // Prepare questions data for bulk import
      const questionsData = uploadedQuestions.map(q => ({
        question: q.question,
        options: q.options,
        correctAnswer: q.correctAnswer,
        explanation: q.explanation
      }));

      // Call bulk API
      await createQuestionsBulk({
        category: categoryId,
        questions: questionsData
      });

      // Refresh questions list
      await fetchQuestions();
      
      // Close modal and reset
      setShowUploadModal(false);
      setUploadedQuestions([]);
      setUploadError('');
      setUploadedFile(null);
      
      // Show success message
      setSuccessMessage(`Successfully imported ${uploadedQuestions.length} questions!`);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error importing questions:', error);
      setUploadError('Error importing questions. Please try again.');
    } finally {
      setUploadLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading questions...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={() => navigate('/dashboard/free-quiz')}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-2"
            aria-label="Back to Categories"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Categories
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Questions for {category?.name || 'Category'}
          </h1>
          {category?.description && (
            <p className="text-gray-600 dark:text-gray-400 mt-1">{category.description}</p>
          )}
        </div>
        <div className="flex gap-3">
          <button
            className="flex items-center px-4 py-2 bg-blue-800 text-white rounded-[24px] hover:bg-blue-900 transition-colors duration-200"
            onClick={() => setShowUploadModal(true)}
            aria-label="Upload Questions"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Upload Questions
          </button>
          <button
            className="flex items-center px-4 py-2 bg-green-800 text-white rounded-[24px] hover:bg-green-900 transition-colors duration-200"
            onClick={() => {
              setShowQuestionForm(true);
              setEditingQuestion(null);
              setQuestionForm({ question: '', options: ['', ''], correctAnswer: 0, explanation: '' });
            }}
            aria-label="Add Question"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Question
          </button>
        </div>
      </div>

      {/* Search Section */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search questions, options, or explanations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-[24px] leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            aria-label="Search Questions"
          />
        </div>
        {searchTerm && (
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Found {filteredQuestions.length} question{filteredQuestions.length !== 1 ? 's' : ''} matching "{searchTerm}"
          </div>
        )}
      </div>

      {/* Questions List */}
      <div className="bg-white dark:bg-gray-800 rounded-[24px] shadow-md overflow-hidden">
        {filteredQuestions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Question</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Options</th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-300 select-none"
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  >
                    <div className="flex items-center gap-1">
                      Uploaded
                      {sortOrder === 'asc' ? (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      ) : (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredQuestions.map((q, index) => (
                  <tr key={q._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-pre-line text-sm text-gray-900 dark:text-gray-100">
                      <div className="font-medium">Q{index + 1}:</div>
                      {q.question}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                      {q.options.map((opt, i) => (
                        <div key={i} className={`${i === q.correctAnswer ? 'font-semibold text-green-600 dark:text-green-400' : ''}`}>
                          {String.fromCharCode(65 + i)}. {opt}
                        </div>
                      ))}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(q.createdAt).toLocaleDateString()}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {new Date(q.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button 
                          className="flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors duration-200"
                          onClick={() => handleEditQuestion(q)}
                          aria-label="Edit Question"
                        >
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit
                        </button>
                        <button 
                          className="flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-400 border border-red-200 dark:border-red-800 rounded hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors duration-200"
                          onClick={() => handleDeleteQuestion(q)}
                          aria-label="Delete Question"
                        >
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : searchTerm ? (
          <div className="text-center py-12">
            <div className="text-gray-500 dark:text-gray-400">
              <svg className="mx-auto h-12 w-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p className="text-lg font-medium">No questions found</p>
              <p className="text-sm">Try adjusting your search terms</p>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-500 dark:text-gray-400">
              <svg className="mx-auto h-12 w-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-lg font-medium">No questions yet</p>
              <p className="text-sm">Add your first question to get started</p>
            </div>
          </div>
        )}
      </div>

      {/* Question Form Modal */}
      {showQuestionForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div ref={modalRef} className="bg-white dark:bg-gray-800 rounded-[24px] shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingQuestion ? 'Edit Question' : 'Add Question'}
              </h2>
              <button
                onClick={() => setShowQuestionForm(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                aria-label="Close Question Form"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleQuestionFormSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Question
                </label>
                <textarea
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-[24px] focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                  placeholder="Enter the question"
                  rows={3}
                  value={questionForm.question}
                  onChange={e => setQuestionForm({ ...questionForm, question: e.target.value })}
                  required
                />
              </div>
              
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Options
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const newOpts = [...questionForm.options, ''];
                      setQuestionForm({ ...questionForm, options: newOpts });
                    }}
                    className="flex items-center px-3 py-1 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-[24px] hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors duration-200"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Option
                  </button>
                </div>
                {questionForm.options.map((opt, i) => (
                  <div key={i} className="mb-2 flex items-center gap-2">
                    <input
                      className="flex-1 p-3 border border-gray-300 dark:border-gray-600 rounded-[24px] focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder={`Option ${String.fromCharCode(65 + i)}`}
                      value={opt}
                      onChange={e => {
                        const newOpts = [...questionForm.options];
                        newOpts[i] = e.target.value;
                        setQuestionForm({ ...questionForm, options: newOpts });
                      }}
                      required
                    />
                    {questionForm.options.length > 2 && (
                      <button
                        type="button"
                        onClick={() => {
                          const newOpts = questionForm.options.filter((_, index) => index !== i);
                          const newCorrectAnswer = i === questionForm.correctAnswer 
                            ? 0 
                            : i < questionForm.correctAnswer 
                              ? questionForm.correctAnswer - 1 
                              : questionForm.correctAnswer;
                          setQuestionForm({ 
                            ...questionForm, 
                            options: newOpts,
                            correctAnswer: newCorrectAnswer
                          });
                        }}
                        className="flex items-center justify-center w-10 h-12 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-[24px] hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors duration-200"
                        title="Remove option"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Correct Answer
                </label>
                <select
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-[24px] focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  value={questionForm.correctAnswer}
                  onChange={e => setQuestionForm({ ...questionForm, correctAnswer: Number(e.target.value) })}
                >
                  {questionForm.options.map((_, i) => (
                    <option key={i} value={i}>{String.fromCharCode(65 + i)}</option>
                  ))}
                </select>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Explanation (Optional)
                </label>
                <textarea
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-[24px] focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                  placeholder="Enter explanation for the correct answer"
                  rows={3}
                  value={questionForm.explanation}
                  onChange={e => setQuestionForm({ ...questionForm, explanation: e.target.value })}
                />
              </div>
              
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowQuestionForm(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-[24px] hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                  aria-label="Cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-800 text-white rounded-[24px] hover:bg-green-900 transition-colors"
                  aria-label="Submit Question Form"
                >
                  {editingQuestion ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div ref={deleteModalRef} className="bg-white dark:bg-gray-800 rounded-[24px] shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Confirm Delete
                </h3>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Are you sure you want to delete this question?
              </p>
              <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-[24px]">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  {itemToDelete.question.length > 100 
                    ? itemToDelete.question.substring(0, 100) + '...' 
                    : itemToDelete.question
                  }
                </p>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setItemToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-[24px] hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                aria-label="Cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-[24px] hover:bg-red-700 transition-colors"
                aria-label="Delete Question"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Questions Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-white dark:bg-gray-800 z-50 overflow-y-auto">
          <div ref={uploadModalRef} className="min-h-screen">
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="max-w-4xl mx-auto p-6 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Upload Questions from Word Document
                </h2>
                <div className="flex items-center gap-3">
                  {uploadedQuestions.length > 0 && (
                    <button
                      onClick={importQuestions}
                      disabled={uploadLoading}
                      className="px-4 py-2 bg-green-800 text-white rounded-[24px] hover:bg-green-900 disabled:bg-gray-400 transition-colors text-sm"
                      aria-label="Import Questions"
                    >
                      {uploadLoading ? 'Importing...' : `Import ${uploadedQuestions.length} Questions`}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowUploadModal(false);
                      setUploadedQuestions([]);
                      setUploadError('');
                    }}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-[24px] hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors text-sm"
                    aria-label="Cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowUploadModal(false);
                      setUploadedQuestions([]);
                      setUploadError('');
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    aria-label="Close Upload Modal"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            
            <div className="p-6 max-w-4xl mx-auto">

            {/* File Upload Section */}
            <div className="mb-6">
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-[24px] p-6 text-center">
                {uploadedFile ? (
                  <div>
                    <svg className="mx-auto h-12 w-12 text-green-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      {uploadedFile.name}
                    </div>
                    <p className="text-sm text-green-600 dark:text-green-400 mb-4">
                      File uploaded successfully! {uploadedQuestions.length > 0 && `(${uploadedQuestions.length} questions found)`}
                    </p>
                    <button
                      onClick={() => {
                        setUploadedFile(null);
                        setUploadedQuestions([]);
                        setUploadError('');
                        const fileInput = document.getElementById('word-file-upload') as HTMLInputElement;
                        if (fileInput) fileInput.value = '';
                      }}
                      className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                    >
                      Upload different file
                    </button>
                  </div>
                ) : (
                  <>
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <label htmlFor="word-file-upload" className="cursor-pointer">
                      <span className="text-lg font-medium text-gray-900 dark:text-white">
                        Upload Word Document (.docx)
                      </span>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                        Click to select a .docx file containing questions
                      </p>
                    </label>
                  </>
                )}
                <input
                  id="word-file-upload"
                  type="file"
                  accept=".docx"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
            </div>

            {/* Format Instructions */}
            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-[24px]">
              <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                Document Format Instructions:
              </h3>
              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                <li>• Separate each question with "---" or "###"</li>
                <li>• Start options with A), B), C), D)</li>
                <li>• Include "Correct Answer: A" (or B, C, D)</li>
                <li>• Add "Explanation: [your explanation]" (optional)</li>
              </ul>
              <div className="mt-3 p-3 bg-white dark:bg-gray-700 rounded text-xs text-gray-600 dark:text-gray-300">
                <p className="font-medium mb-1">Example:</p>
                <pre className="whitespace-pre-wrap">
{`Question 1: What is the capital of France?
A) London
B) Paris
C) Berlin
D) Madrid
Correct Answer: B
Explanation: Paris is the capital of France.

---

Question 2: Which crop is known as the "King of Cereals"?
A) Wheat
B) Rice
C) Maize
D) Barley
Correct Answer: B
Explanation: Rice is often called the "King of Cereals".`}
                </pre>
              </div>
            </div>

            {/* Error Display */}
            {uploadError && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-[24px]">
                <p className="text-sm text-red-800 dark:text-red-200">{uploadError}</p>
              </div>
            )}

            {/* Loading State */}
            {uploadLoading && (
              <div className="mb-6 text-center">
                <div className="inline-flex items-center px-4 py-2 text-sm text-blue-700 dark:text-blue-300">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing document...
                </div>
              </div>
            )}

            {/* Preview Questions */}
            {uploadedQuestions.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Preview ({uploadedQuestions.length} questions found)
                </h3>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {uploadedQuestions.map((q, index) => (
                    <div key={index} className="p-4 border border-gray-200 dark:border-gray-700 rounded-[24px] bg-gray-50 dark:bg-gray-700">
                      <div className="font-medium text-gray-900 dark:text-white mb-2">
                        Question {index + 1}: {q.question}
                      </div>
                      <div className="space-y-1 mb-2">
                        {q.options.map((opt: string, i: number) => (
                          <div key={i} className={`text-sm ${i === q.correctAnswer ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-gray-600 dark:text-gray-300'}`}>
                            {String.fromCharCode(65 + i)}. {opt}
                          </div>
                        ))}
                      </div>
                      {q.explanation && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          <span className="font-medium">Explanation:</span> {q.explanation}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-[24px] shadow-xl p-6 max-w-md w-full mx-4 transform transition-all">
            <div className="text-center">
              {/* Success Icon */}
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/20 mb-4">
                <svg className="h-8 w-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              {/* Success Message */}
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Success!
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                {successMessage}
              </p>
              
              {/* Action Button */}
              <button
                onClick={() => setShowSuccessModal(false)}
                className="w-full px-4 py-2 bg-green-800 text-white rounded-[24px] hover:bg-green-900 transition-colors duration-200"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionsPage; 